import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnInit,
  Renderer2,
  ViewChild
} from "@angular/core";
import {NgClass} from "@angular/common";
import {OnDestroy$} from "@pdtec/ngx-observable-lifecycle";
import { environment } from '../../../../environments/environment';
import {WindowRefService} from "../../../services/window-ref.service";
import {EventBusService} from "../../../services/event-bus.service";
import {HotelToursSearchService} from "../../../services/search/hotel-tours-search.service";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../../config/hotel-tours-list-page.config";
import {filter, takeUntil} from "rxjs/operators";
import {HttpHotelToursSearchService} from "../../../services/search/hotel-tours-requests.service";
import {Subject} from "rxjs";
import {HotelToursResultFilters,} from "../../../types/interfaces/hotel-tour-search";
import {HotelToursFiltersSkeletonComponent} from "./filters-skeleton/filters-skeleton.component";
import {NgModalComponent} from "../../ui/modal/modal.component";


@Component({
  selector: 'app-hotel-tours-filters',
  templateUrl: environment.mobile ? './filters.component.mobile.html' : './filters.component.html',
  styleUrls: environment.mobile ? ['./filters.component.mobile.scss'] : ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HotelToursFiltersSkeletonComponent, NgModalComponent, NgClass],
  standalone: true,
})
export class HotelToursFiltersComponent extends OnDestroy$ implements OnInit {
  @ViewChild(NgModalComponent) ngModal!: NgModalComponent;
  filters: HotelToursResultFilters[];
  private filtersMaster: HotelToursResultFilters[] = null;
  isShowSkeleton: boolean = true;
  activeModalConfiguration: string = 'bottom';
  openedFilter: string | null = null;
  filterList: any;
  allFilterList: HotelToursResultFilters[];
  selectedFilters: { key: string; value: string }[] = [];
  checkedFilters: { key: string; value: string }[] = [];

  private destroy$ = new Subject<void>();
  private resetButtonListener: () => void;
  private applyButtonListener: () => void;
  private isFiltersRequestStarted = false;

  constructor(
    private windowService: WindowRefService,
    private eventBusService: EventBusService,
    private searchService: HotelToursSearchService,
    private httpSearchService: HttpHotelToursSearchService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
  ) {
    super();
  }


  ngOnInit() {
    this.searchService.newSearchStarted$
      .pipe(
        filter(searchId => !!searchId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const searchSource = this.searchService.getLastSearchSource();
        const shouldPreserveFilters =
          searchSource === 'filters'
          && !!this.filtersMaster?.length;

        if (!shouldPreserveFilters) {
          this.filtersMaster = null;
          this.filters = null;
          this.openedFilter = null;
          this.selectedFilters = [];
          this.checkedFilters = [];
        }

        this.isShowSkeleton = !shouldPreserveFilters;
        this.isFiltersRequestStarted = false;
        this.cdr.detectChanges();
      });

    this.searchService.isLoadFilters$
      .pipe(
        filter(searchId => !!searchId),
        takeUntil(this.destroy$)
      )
      .subscribe((searchId) => {
        this.isFiltersRequestStarted = true;
        this.httpSearchService.getFilters(searchId)
          .subscribe({
            next: (response) => {
              this.applyFiltersResponse(response.filters, false);
              this.initOnboarding();
              this.isShowSkeleton = false;
              this.cdr.detectChanges();
            },
            error: (error) => {
              this.isShowSkeleton = false;
              this.cdr.detectChanges();
              console.error('Ошибка при запросе фильтров:', error);
            }
          })
      });

    this.httpSearchService.isFindFirstTours$.pipe(
      filter(searchId => !!searchId),
      takeUntil(this.destroy$)
    )
      .subscribe((searchId) => {
        this.isFiltersRequestStarted = true;
        this.httpSearchService.getFilters(searchId)
          .subscribe({
            next: (response) => {
              this.applyFiltersResponse(response.filters, false);
              this.initOnboarding();
              this.isShowSkeleton = false;
              this.cdr.detectChanges();
            },
            error: (error) => {
              this.isShowSkeleton = false;
              this.cdr.detectChanges();
              console.error('Ошибка при запросе фильтров:', error);
            }
          })
      });

    this.searchService.searchIsDone$
      .pipe(
        filter(searchId => !!searchId),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isFiltersRequestStarted) {
          this.isShowSkeleton = false;
          this.cdr.detectChanges();
        }
      });

    this.eventBusService.isCloseModal$
      .pipe(
        filter((modal: HTMLElement) => {
          return modal.classList.contains('filters-modal')
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.openedFilter = null;
        this.cdr.detectChanges();
      })
  }

  ngOnDestroy() {
    if (this.resetButtonListener) {
      this.resetButtonListener();
      this.resetButtonListener = null;
    }
    if (this.applyButtonListener) {
      this.applyButtonListener();
      this.applyButtonListener = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click')
  closeDropdownOnOutsideClick() {
    if (environment.mobile) {
      return;
    }
    if (this.openedFilter) {
      this.openedFilter = null;
      this.cdr.detectChanges();
    }
  }

  toggleFilterDropdown(filter: any, event: MouseEvent) {
    event.stopPropagation();
    this.openedFilter = this.openedFilter === filter.filterKey ? null : filter.filterKey;
    this.cdr.detectChanges();
  }

  filtersModalConfiguration() {
    let filterType = '';
    if (this.openedFilter === 'airlines') {
      filterType = 'airline';
    }
    if (this.openedFilter === 'mealIds') {
      filterType = 'meal';
    }

    if (this.activeModalConfiguration === 'bottom') {
      return {
        content: {
          htmlDetachSelectorId: 'filterModal',
        },
        parentCssClass: 'filters-modal',
        footer: {
          buttonLabel: 'Применить',
          buttonCssClass: 'btn-apply'
        },
        onBeforeClose: () => {
          this.unselectAllFilters();
        },
        onAfterShow: () => {
          this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('filter_modal_open', {
            type: filterType
          });
        },
      }
    }

    if (this.activeModalConfiguration === 'full') {
      return {
        content: {
          htmlDetachSelectorId: 'filtersModal',
        },
        parentCssClass: 'filters-modal',
        footer: {
          buttonLabel: 'Применить',
          buttonCssClass: 'btn-apply'
        },
        type: 'full',
        header: {
          theme: 'primary',
          html: '<button type="button" class="ng-reset-filters"></button>',
        },
        onAfterShow: () => {
          const resetButton = document.querySelector('.ng-reset-filters');
          if (resetButton) {
            this.resetButtonListener = this.renderer.listen(resetButton, 'click', (event) => {
              this.resetFilters(event);
            });
          }
          this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('filter_modal_open', {
            type: 'all'
          });
        },
        onBeforeClose: () => {
          if (this.resetButtonListener) {
            this.resetButtonListener();
            this.resetButtonListener = null;
          }

          this.unselectAllFilters();
        }
      }
    }
  }

  openFilterModal(key, filter = null) {
    if (!this.filters?.length) {
      return;
    }
    this.activeModalConfiguration = key;

    if (filter) {
      this.openedFilter = filter.filterKey;
      this.setModalTitle(filter.name);
      const filterValues = this.filters.find(f => f.filterKey === filter.filterKey);

      if (filterValues) {
        this.filterList = filterValues.values;
      }

      if (key === 'mealIds') {
        this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_meal_filter_open');
      }

      if (key === 'airlines') {
        this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_airline_filter_open');
      }
    } else {
      this.openedFilter = 'all';
      this.setModalTitle('Фильтры');
      this.allFilterList = this.filters;
      this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_all_filters_open');
    }
    this.checkedFilters = this.cloneFilters(this.selectedFilters);
    this.cdr.detectChanges();
    this.ngModal.open();

    const button = document.querySelector('.modal2 .btn-apply');
    if (button) {
      if (this.applyButtonListener) {
        this.applyButtonListener();
      }
      this.applyButtonListener = this.renderer.listen(button, 'click', () => this.onApply());
    }
  }

  setModalTitle(name) {
    this.eventBusService.changeModalTitle$.next(name);
  }

  selectFilter(event: Event, filterGroupKey: string, applyImmediately: boolean = false) {
    const checkbox = event.target as HTMLInputElement;
    const filterValue = checkbox.value;

    if (checkbox.checked) {
      const isAlreadySelected = this.checkedFilters.some(
        (filter) => filter.key === filterGroupKey && filter.value === filterValue
      );

      if (!isAlreadySelected) {
        this.checkedFilters.push({key: filterGroupKey, value: filterValue});
      }
    } else {
      this.checkedFilters = this.checkedFilters.filter(
        (filter) => !(filter.key === filterGroupKey && filter.value === filterValue)
      );
    }

    if (this.ngModal?.configuration?.content?.htmlDetachSelectorId === 'filtersModal') {
      if (filterGroupKey === 'mealIds') {
        this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('select_meal_type_in_filters_modal');
      }

      if (filterGroupKey === 'airlines') {
        this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('select_airline_in_filters_modal');
      }
    } else if (this.ngModal?.configuration?.content?.htmlDetachSelectorId) {
      if (filterGroupKey === 'mealIds') {
        this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_select_meal_type');
      }

      if (filterGroupKey === 'airlines') {
        this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_select_airline');
      }
    }
    if (applyImmediately) {
      this.onApply();
    }
  }

  isFilterSelected(filterGroupKey: string, filterValue: string): boolean | null {
    const isSelected = this.selectedFilters.some(
      (filter) => filter.key === filterGroupKey && filter.value === filterValue
    );

    return isSelected ? true : null;
  }

  isFilterActive(filter: any): boolean {
    return this.selectedFilters.some(selected => selected.key === filter.filterKey);
  }

  getFilterDisplayName(filter: any): string {
    const selected = this.selectedFilters.filter(f => f.key === filter.filterKey);
    if (!selected.length) {
      return filter.name;
    }
    const firstValue = filter.values?.find((v) => v.filterValue === selected[0].value);
    const firstName = firstValue?.viewValue || filter.name;
    if (selected.length === 1) {
      return firstName;
    }
    return `${firstName} +${selected.length - 1}`;
  }

  getAirlineImageSrc(filterValue: string): string {
    if (!filterValue) {
      return '';
    }
    const upperCasedValue = filterValue.toUpperCase();
    return `/x-assets/img/airlines/round/${upperCasedValue}.png`;
  }

  resetFilters(event: Event, filter: any = null) {
    event.stopPropagation();
    if (filter) {
      this.selectedFilters = this.selectedFilters.filter(
        selected => selected.key !== filter.filterKey
      );
      this.checkedFilters = this.cloneFilters(this.selectedFilters);
    } else {
      this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_reset_filters_in_all_filters_modal');
      this.selectedFilters = [];
      this.checkedFilters = [];
    }

    this.cdr.detectChanges();
    this.onApply();
  }

  unselectAllFilters(): void {
    this.checkedFilters = this.cloneFilters(this.selectedFilters);

    const checkboxes = document.querySelectorAll('.filter-list input[type="checkbox"]');
    checkboxes.forEach((checkbox: HTMLInputElement) => {
      this.renderer.setProperty(checkbox, 'checked', false);
    });
  }

  onApply() {
    this.selectedFilters = this.cloneFilters(this.checkedFilters);
    const selectedFilters = this.selectedFilters;
    this.searchService.setSelectedFilters(selectedFilters);
    this.searchService.startNewSearchByHotelId(this.searchService.getLastSearchParams(), 'filters');


    if (this.ngModal?.configuration?.content?.htmlDetachSelectorId === 'filtersModal') {
      this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_apply_in_filters_modal');
    }

    if (this.openedFilter === 'mealIds') {
      this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_apply_meal_type');
    }

    if (this.openedFilter === 'airlines') {
      this.windowService.nativeWindow.brxTracking.sendAnalyticsEvent('click_apply_airline');
    }
  }

  markForCheckFilters () {
    const params = this.searchService.getLastSearchParams();

    if (params.airlines) {
      params.airlines.forEach(airline => {
        // Проверяем, есть ли уже такой фильтр
        const exists = this.checkedFilters.some(filter => filter.key === 'airlines' && filter.value === airline);

        if (!exists) {
          this.checkedFilters.push({ key: 'airlines', value: airline });
        }
      });
    }

    if (params.meals) {
      params.meals.forEach(meal => {
        // Проверяем, есть ли уже такой фильтр
        const exists = this.checkedFilters.some(filter => filter.key === 'mealIds' && filter.value === meal);

        if (!exists) {
          this.checkedFilters.push({ key: 'mealIds', value: meal });
        }
      });
    }

    this.selectedFilters = this.cloneFilters(this.checkedFilters);
  }

  private cloneFilters(filters: { key: string; value: string }[]): { key: string; value: string }[] {
    return (filters ?? []).map((filter) => ({ ...filter }));
  }

  private applyFiltersResponse(nextFilters: HotelToursResultFilters[], shouldMerge: boolean) {
    this.filtersMaster = shouldMerge ? this.mergeFilters(this.filtersMaster, nextFilters) : nextFilters;
    this.filters = this.filtersMaster;
    this.syncSelectedFiltersWithAvailableFilters(this.filtersMaster ?? []);
    this.markForCheckFilters();
  }

  private syncSelectedFiltersWithAvailableFilters(filters: HotelToursResultFilters[]) {
    const availableFilters = new Set<string>();

    filters.forEach((filterGroup) => {
      filterGroup.values.forEach((filterValue) => {
        availableFilters.add(`${filterGroup.filterKey}:${filterValue.filterValue}`);
      });
    });

    this.checkedFilters = this.checkedFilters.filter((filter) => availableFilters.has(`${filter.key}:${filter.value}`));
    this.selectedFilters = this.selectedFilters.filter((filter) => availableFilters.has(`${filter.key}:${filter.value}`));
  }

  private mergeFilters(existingFilters: HotelToursResultFilters[] | null, nextFilters: HotelToursResultFilters[]) {
    if (!existingFilters?.length) {
      return nextFilters;
    }

    const nextFiltersMap = new Map(nextFilters.map((filterGroup) => [filterGroup.filterKey, filterGroup]));
    const mergedFilters = existingFilters.map((existingGroup) => {
      const nextGroup = nextFiltersMap.get(existingGroup.filterKey);

      if (!nextGroup) {
        return existingGroup;
      }

      const mergedValuesMap = new Map(
        existingGroup.values.map((value) => [value.filterValue, value])
      );

      nextGroup.values.forEach((value) => {
        mergedValuesMap.set(value.filterValue, value);
      });

      return {
        ...nextGroup,
        values: Array.from(mergedValuesMap.values()),
      };
    });

    nextFilters.forEach((nextGroup) => {
      const exists = mergedFilters.some((filterGroup) => filterGroup.filterKey === nextGroup.filterKey);

      if (!exists) {
        mergedFilters.push(nextGroup);
      }
    });

    return mergedFilters;
  }

  initOnboarding() {
    history.scrollRestoration = 'manual';
    document.dispatchEvent(new CustomEvent('_show-onboarding', {
      detail: {
        onboardingId: 'mobile_hotel_tours_filters'
      },
    }));
  }
}
